import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CertificationTracker = ({ userRole = 'Developer', onViewCertificate, onShareAchievement }) => {
  const certifications = [
    {
      id: 1,
      title: "IoT Fundamentals",
      category: "Technical Skills",
      status: "earned",
      earnedDate: new Date(2025, 7, 15),
      badgeColor: "bg-blue-500",
      icon: "Cpu",
      description: "Completed comprehensive IoT sensor integration and data collection modules",
      skills: ["Sensor Networks", "Data Collection", "Protocol Design"],
      credentialId: "IOT-2025-001234"
    },
    {
      id: 2,
      title: "Smart City Design Principles",
      category: "Urban Planning",
      status: "earned",
      earnedDate: new Date(2025, 8, 2),
      badgeColor: "bg-green-500",
      icon: "Building2",
      description: "Mastered urban planning concepts and smart city infrastructure design",
      skills: ["Urban Planning", "Infrastructure", "Sustainability"],
      credentialId: "SCD-2025-005678"
    },
    {
      id: 3,
      title: "Data Science for Cities",
      category: "Analytics",
      status: "in-progress",
      progress: 75,
      badgeColor: "bg-purple-500",
      icon: "BarChart3",
      description: "Advanced data analysis techniques for urban data and city metrics",
      skills: ["Data Analysis", "Statistics", "Visualization"],
      estimatedCompletion: new Date(2025, 8, 25)
    },
    {
      id: 4,
      title: "AI in Urban Systems",
      category: "Artificial Intelligence",
      status: "locked",
      badgeColor: "bg-orange-500",
      icon: "Brain",
      description: "Machine learning applications in smart city infrastructure",
      skills: ["Machine Learning", "AI", "Predictive Analytics"],
      prerequisites: ["Data Science for Cities"]
    },
    {
      id: 5,
      title: "GIS and Spatial Analysis",
      category: "Geographic Systems",
      status: "available",
      badgeColor: "bg-teal-500",
      icon: "Map",
      description: "Geographic information systems for urban planning and analysis",
      skills: ["GIS", "Spatial Analysis", "Mapping"],
      estimatedDuration: "3 weeks"
    }
  ];

  const earnedCertifications = certifications?.filter(cert => cert?.status === 'earned');
  const inProgressCertifications = certifications?.filter(cert => cert?.status === 'in-progress');
  const availableCertifications = certifications?.filter(cert => cert?.status === 'available');

  const formatDate = (date) => {
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'earned': return 'Award';
      case 'in-progress': return 'Clock';
      case 'locked': return 'Lock';
      default: return 'Play';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'earned': return 'text-success';
      case 'in-progress': return 'text-warning';
      case 'locked': return 'text-muted-foreground';
      default: return 'text-primary';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Certification Tracker</h2>
        <div className="flex items-center space-x-2">
          <div className="text-center">
            <div className="text-xl font-bold text-success">{earnedCertifications?.length}</div>
            <div className="text-xs text-muted-foreground">Earned</div>
          </div>
          <div className="w-px h-8 bg-border mx-2"></div>
          <div className="text-center">
            <div className="text-xl font-bold text-warning">{inProgressCertifications?.length}</div>
            <div className="text-xs text-muted-foreground">In Progress</div>
          </div>
        </div>
      </div>
      {/* Earned Certifications */}
      {earnedCertifications?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
            <Icon name="Award" size={16} className="mr-2 text-success" />
            Earned Certifications
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {earnedCertifications?.map((cert) => (
              <div key={cert?.id} className="border border-border rounded-lg p-4 bg-success/5">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 ${cert?.badgeColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={cert?.icon} size={20} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground text-sm mb-1">{cert?.title}</h4>
                    <p className="text-xs text-muted-foreground mb-2">{cert?.category}</p>
                    <p className="text-xs text-success mb-2">
                      Earned on {formatDate(cert?.earnedDate)}
                    </p>
                    <div className="flex space-x-1">
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => onViewCertificate(cert)}
                        iconName="Eye"
                        iconPosition="left"
                      >
                        View
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => onShareAchievement(cert)}
                        iconName="Share"
                        iconPosition="left"
                      >
                        Share
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* In Progress Certifications */}
      {inProgressCertifications?.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
            <Icon name="Clock" size={16} className="mr-2 text-warning" />
            In Progress
          </h3>
          <div className="space-y-3">
            {inProgressCertifications?.map((cert) => (
              <div key={cert?.id} className="border border-border rounded-lg p-4 bg-warning/5">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 ${cert?.badgeColor} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon name={cert?.icon} size={20} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-foreground text-sm">{cert?.title}</h4>
                        <p className="text-xs text-muted-foreground">{cert?.category}</p>
                      </div>
                      <span className="text-xs font-medium text-warning">{cert?.progress}%</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2 mb-2">
                      <div 
                        className="bg-warning h-2 rounded-full transition-all duration-300"
                        style={{ width: `${cert?.progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Est. completion: {formatDate(cert?.estimatedCompletion)}
                    </p>
                    <Button
                      size="xs"
                      variant="default"
                      iconName="Play"
                      iconPosition="left"
                    >
                      Continue
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Available Certifications */}
      {availableCertifications?.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3 flex items-center">
            <Icon name="Play" size={16} className="mr-2 text-primary" />
            Available to Start
          </h3>
          <div className="space-y-3">
            {availableCertifications?.map((cert) => (
              <div key={cert?.id} className="border border-border rounded-lg p-4 hover:border-primary/20 transition-all duration-200">
                <div className="flex items-start space-x-3">
                  <div className={`w-10 h-10 ${cert?.badgeColor} rounded-lg flex items-center justify-center flex-shrink-0 opacity-60`}>
                    <Icon name={cert?.icon} size={20} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-medium text-foreground text-sm">{cert?.title}</h4>
                        <p className="text-xs text-muted-foreground">{cert?.category}</p>
                      </div>
                      <Icon name={getStatusIcon(cert?.status)} size={16} className={getStatusColor(cert?.status)} />
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{cert?.description}</p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {cert?.skills?.slice(0, 3)?.map((skill, index) => (
                        <span key={index} className="px-2 py-1 bg-muted text-muted-foreground text-xs rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">
                        Duration: {cert?.estimatedDuration}
                      </span>
                      <Button
                        size="xs"
                        variant="default"
                        disabled={cert?.status === 'locked'}
                        iconName="Play"
                        iconPosition="left"
                      >
                        {cert?.status === 'locked' ? 'Locked' : 'Start'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificationTracker;